import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import {
  StakeReportDetailContent,
  formatDate,
  getSubscriptionId,
  userFullName,
} from "./stakeReportShared";
import "../Home/Homepage.css";
import "../Trading/AllOpenOrdersPage.css";
import "./StakingFullReportPage.css";

/**
 * Always loads full detail via subscription_id.
 * List rows are summaries only (detail_loaded=false) — do not reuse list payload here.
 */
const StakingFullReportDetailPage = () => {
  const { subscriptionId } = useParams();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!subscriptionId) {
      setError("Missing subscription id.");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchDetail = async () => {
      setLoading(true);
      setError("");
      setRow(null);
      try {
        const res = await AdminService.getStakingFullReport({
          subscription_id: subscriptionId,
        });
        const list = Array.isArray(res?.data) ? res.data : [];
        const match =
          list.find((r) => String(getSubscriptionId(r)) === String(subscriptionId)) ??
          (list.length === 1 ? list[0] : null) ??
          (res?.data && !Array.isArray(res.data) ? res.data : null);

        if (cancelled) return;

        if (match) {
          setRow(match);
        } else {
          setError(res?.message || "Stake report not found.");
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err?.response?.data?.message || err?.message || "Failed to load stake report."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [subscriptionId]);

  const staker = row?.staker;

  return (
    <main className="dashboard-main dashboard-subpage stake-report-detail-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <Link to="/dashboard/staking/stake-report">Stake Report</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Details</span>
          </p>
          <h1 className="dashboard-hero__title">Stake report details</h1>
          <p className="dashboard-hero__subtitle">
            {row
              ? `${userFullName(staker)}${staker?.email ? ` · ${staker.email}` : ""}`
              : "Full subscription, ROI and referral payout breakdown"}
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-file-invoice" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="stake-report-detail-page__toolbar">
          <Link to="/dashboard/staking/stake-report" className="stake-report-back-btn">
            <i className="fas fa-arrow-left" /> Back to list
          </Link>
          {subscriptionId ? (
            <span className="stake-report-detail-page__id">
              ID: <code>{subscriptionId}</code>
            </span>
          ) : null}
          {row?.detail_loaded === false ? (
            <span className="openorders-stat" style={{ margin: 0 }}>
              Waiting for full detail…
            </span>
          ) : null}
        </div>

        {loading ? (
          <div className="openorders-empty">
            <i className="fas fa-spinner fa-spin" /> Loading full details…
          </div>
        ) : error ? (
          <div className="stake-report-detail-page__error">
            <i className="fas fa-exclamation-circle" />
            <p>{error}</p>
            <Link to="/dashboard/staking/stake-report" className="stake-report-back-btn">
              Return to stake report
            </Link>
          </div>
        ) : row ? (
          <section className="openorders-section stake-report-detail-page__card">
            <StakeReportDetailContent row={row} />
            <p className="stake-report-detail-page__meta">
              Last updated: {formatDate(row.updated_at)}
              {row.detail_loaded != null ? (
                <>
                  {" "}
                  · detail_loaded: <code>{String(row.detail_loaded)}</code>
                </>
              ) : null}
            </p>
          </section>
        ) : null}
      </div>
    </main>
  );
};

export default StakingFullReportDetailPage;
